import { forwardRef, useState, useEffect, useCallback } from 'react';
import styles from './Wheel.module.scss';
import { Sector } from '../Sector/Sector';
import { IWheelOffurtunePrize } from '../WheelOfFortune';

interface WheelProps {
    sectors: {
        hue: number;
        prize: IWheelOffurtunePrize;
        angle: number;
        sectorAngle: number;
    }[];
    isSpinning: boolean;
    winnerIndex: number;
    onAnimationEnd: () => void;
}

export const Wheel = forwardRef<HTMLDivElement, WheelProps>(({ 
    sectors, 
    isSpinning, 
    winnerIndex, 
    onAnimationEnd 
}, ref) => {
    const [currentRotation, setCurrentRotation] = useState(0);
    const totalRotations = 5;
    const sectorAngle = 360 / sectors.length;

    const calculateTargetAngle = useCallback((winnerIdx: number) => {
        const normalizedCurrentRotation = currentRotation % 360;
        const targetSectorAngle = 360 - ((winnerIdx + 1) * sectorAngle + sectorAngle / 2);

        let additionalRotations = totalRotations;
        if (normalizedCurrentRotation > targetSectorAngle)
            additionalRotations += 1;

        return currentRotation + additionalRotations * 360 + (targetSectorAngle - normalizedCurrentRotation);
    }, [currentRotation, sectorAngle, totalRotations]);

    const [targetAngle, setTargetAngle] = useState(calculateTargetAngle(winnerIndex));

    useEffect(() => {
        if (isSpinning) {
            setTargetAngle(calculateTargetAngle(winnerIndex));
        }
    }, [isSpinning, winnerIndex, calculateTargetAngle]);

    const handleTransitionEnd = () => {
        if (isSpinning) {
            setCurrentRotation(targetAngle % 360);
            onAnimationEnd();
        }
    };

    return (
        <div className={styles.wheelWrapper}>
            <div 
                className={styles.wheel}
                ref={ref}
                style={{ 
                    transform: `rotate(${isSpinning ? targetAngle : currentRotation}deg)`,
                    transition: isSpinning 
                        ? `transform 4s cubic-bezier(0.17, 0.67, 0.12, 0.99)`
                        : 'none'
                }}
                onTransitionEnd={handleTransitionEnd}
            >
                {Array.from({ length: sectors.length }).map((_, i) => (
                    <div 
                        key={`divider-${i}`}
                        className={styles.sectorDivider}
                        style={{
                            transform: `rotate(${i * sectorAngle}deg)`
                        }}
                    />
                ))}
                
                {sectors.map((sector, index) => (
                    <Sector 
                        key={index}
                        hue={sector.hue}
                        prize={sector.prize.node}
                        angle={sector.angle}
                        isWinner={index === winnerIndex}
                    />
                ))}
            </div>
            <div className={styles.wheelCenter}></div>
            <div className={styles.wheelArrow}></div>
        </div>
    );
});